import React from 'react';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  Card,
  TextAction,
  useMediaQuery,
  queryHelpers,
} from '@leagueplatform/genesis-core';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';
import { useStaticAsset } from '@leagueplatform/asset-config';

import { GLOSSARY_ASSET_KEYS } from '../../types/glossary.types';
import GlossaryContent from '../../assets/json/glossary-content.json';

export const PageHeader = ({
  showBackButton = true,
}: {
  showBackButton?: boolean;
}) => {
  const navigate = useNavigate();

  const headerIcon = useStaticAsset(GLOSSARY_ASSET_KEYS.GLOSSARY_HEADER_ICON);
  const leftArrow = useStaticAsset(GLOSSARY_ASSET_KEYS.LEFT_ARROW);
  const isBetweenDownSizeMobile = useMediaQuery(
    queryHelpers.between([344, 399]),
  );

  const isUp400 = useMediaQuery(queryHelpers.up(400));
  const is390 = useMediaQuery(queryHelpers.between([390, 393]));
  const isBetween359 = useMediaQuery(queryHelpers.between([300, 359]));

  const checkResolution = () => {
    if (isUp400) {
      return '55px';
    }
    if (isBetween359) {
      return '5px';
    }
    if (is390) {
      return '-32px';
    }
    return '-20px';
  };

  return (
    <Box
      css={
        {
          backgroundColor: '$surfaceBackgroundPrimary',
          minHeight: '12vh',
          width: '100%',
          '@desktop': {
            borderRadius: '0px',
            borderBottom: '0px solid #E0E3E6',
          },
          '@mobile': {
            borderRadius: '0px',
            borderBottom: '1px solid #E0E3E6',
          },
        } as GDSStyleObject
      }
    >
      <Card.Flat
        css={
          {
            backgroundColor: '$surfaceBackgroundSecondary',
            padding: '$oneAndHalf',
            height: '184px',
            '@mobile': {
              height: '164px',
            },
          } as GDSStyleObject
        }
      >
        <Card.Section
          css={
            {
              boxSizing: 'border-box',
              width: '100%',
              maxWidth: '1340px',
              '@desktop': {
                margin: '0px auto',
              },
              '@mobile': {
                padding: '$none',
                // marginLeft: '-25px',
              },
            } as GDSStyleObject
          }
        >
          <Box
            css={{
              display: 'flex',
              '@desktop': {
                marginLeft: '32px',
                marginTop: '-17px',
              },
              '@mobile': {
                flexDirection: 'row-reverse',
                height: '116px',
              },
              '@tablet': {
                height: '40px',
                marginTop: '$oneAndHalf',
              },
            }}
          >
            {showBackButton && (
              <TextAction
                onClick={() => navigate(-1)}
                as="button"
                css={{
                  color: '$onSurfaceIconPrimary',
                  marginBottom: '$half',
                  position: 'relative',
                  '@desktop': {
                    top: '-12px',
                    left: '-8px',
                  },
                  '@mobile': {
                    order: 1,
                    top: isBetweenDownSizeMobile ? '-35px' : '-20px',
                    left: isBetweenDownSizeMobile ? checkResolution() : '-35px',
                  },
                  '@media only screen and (max-width: 389px) and  (min-width: 361px) ':
                    {
                      left: '5px',
                    },
                  '@media only screen and (width: 390px) and (width: 393px)': {
                    left: '-30px',
                  },
                  '@media only screen and (width: 393px)': {
                    left: '-10px',
                  },
                  '@media only screen and (width: 430px)': {
                    left: '-45px',
                  },
                  '@media only screen and (width: 360px)': {
                    left: '20px',
                  },
                  '@media only screen and (width: 390px)': {
                    left: '-5px',
                  },
                  '@media only screen and (width: 355px)': {
                    left: '26px',
                  },
                  '@mobileLandscape': {
                    top: '-35px',
                    right: '35px',
                  },
                  '@laptop': {
                    top: '-24px',
                    right: '0px',
                  },
                }}
              >
                <img
                  src={leftArrow as string}
                  alt="Back"
                  style={{
                    cursor: 'pointer',
                  }}
                />
              </TextAction>
            )}
            <TextAction
              css={{
                '@desktop': {
                  paddingTop: '23px',
                },
                '@mobile': {
                  top: isBetweenDownSizeMobile ? '8px' : '20px',
                  left: isBetweenDownSizeMobile ? '0px' : '0px',
                },
              }}
            >
              <img
                src={headerIcon as string}
                style={{ width: '80px', height: '80px', marginLeft: '24px' }}
                alt="Back"
              />
            </TextAction>
            <Box>
              <HeadingText
                level="1"
                size={{ '@initial': 'xxl', '@mobile': 'xl' }}
                css={{
                  position: 'relative',
                  fontWeight: '$headingOne',
                  '@desktop': {
                    top: '50px',
                    left: '32px',
                    fontSize: '$headingOne',
                  },
                  '@mobile': {
                    paddingLeft: '$none',
                    top: isBetweenDownSizeMobile ? '53px' : '60px',
                    left: isBetweenDownSizeMobile ? checkResolution() : '-60px',
                    lineHeight: '28px',
                    paddingBottom: '15px',
                    width: '213px',
                    fontSize: '24px !important',
                  },
                  '@media only screen and (max-width: 389px) and  (min-width: 361px) ':
                    {
                      left: '-18px',
                    },
                  '@media only screen and (width: 390px) and (width: 393px)': {
                    left: '-30px',
                  },
                  '@media only screen and (width: 393px)': {
                    left: '-36px',
                  },
                  '@media only screen and (width: 430px)': {
                    left: '-69px',
                  },
                  '@media only screen and (width: 360px)': {
                    left: '-5px',
                  },
                  '@media only screen and (width: 390px)': {
                    left: '-32px',
                  },
                  '@tablet': {
                    marginTop: '10px',
                    top: '0px',
                    marginLeft: '$oneAndHalf',
                  },
                  '@laptop': {
                    top: '15px',
                    left: '32px',
                  },
                }}
              >
                {GlossaryContent.GLOSSARY.GLOSSARYSECTIONTITLE}
              </HeadingText>
            </Box>
          </Box>
        </Card.Section>
      </Card.Flat>
    </Box>
  );
};
